import { DateTime, Interval } from 'luxon';
import { useEffect, useState } from 'react';
import { StringParam, useQueryParam, withDefault } from 'use-query-params';

import { DateRange, Period } from '../types';
import { getDateRanges } from '../utils';

const getDateIntervalFromDateRange = (dateRange: DateRange) => {
  const { from, to } = dateRange;
  if (!to) {
    if (!from) {
      return Interval.invalid('Both `from` and `to` from date range are missing!');
    }
    return Interval.fromDateTimes(from, from);
  }
  if (!from) {
    return Interval.fromDateTimes(to, to);
  }

  return Interval.fromDateTimes(from, to);
};

const getValidDateRange = (from: string, to: string) => {
  const rawUrlInterval = getDateIntervalFromDateRange({
    from: DateTime.max(DateTime.fromISO(from)).toJSDate(),
    to: DateTime.min(DateTime.fromISO(to)).toJSDate(),
  });

  const maximumRangeInterval = Interval.fromDateTimes(
    getDateRanges().disabledDays.before,
    getDateRanges().disabledDays.after,
  );

  // single day interval doesn't work with Interval.intersection(), so we handle it separately
  const singleDayInterval =
    rawUrlInterval.isValid &&
    rawUrlInterval.end.equals(rawUrlInterval.start) &&
    maximumRangeInterval.contains(rawUrlInterval.start) &&
    rawUrlInterval;

  const intervalWithinAllowedRange = rawUrlInterval.intersection(maximumRangeInterval) || singleDayInterval;

  if (intervalWithinAllowedRange && intervalWithinAllowedRange.isValid) {
    return {
      from: intervalWithinAllowedRange.start.toJSDate(),
      to: intervalWithinAllowedRange.end.toJSDate(),
    };
  } else {
    return null;
  }
};

const parsePeriod = (period: Period | string | null) => {
  const dateRanges = getDateRanges();
  const defaultDateRange = dateRanges.sevenDaysAgoDateRange;
  const dateRange = dateRanges[period as Period];

  if (dateRange) {
    return dateRange;
  }

  const [from, to] = period?.split('--') ?? [];

  return getValidDateRange(from, to) ?? defaultDateRange;
};

const serializePeriod = (dateRange: DateRange) => {
  const dateInterval = getDateIntervalFromDateRange(dateRange);

  if (!dateInterval.isValid) {
    throw new Error('Date interval is invalid with the following reason: ' + dateInterval.invalidReason);
  }
  return dateRange.name ?? `${dateInterval.start.toISODate()}--${dateInterval.end.toISODate()}`;
};

const rangeToInterval = (dateRange: DateRange) => {
  const dateInterval = getDateIntervalFromDateRange(dateRange);

  if (!dateInterval.isValid) {
    throw new Error('Date interval is invalid with the following reason: ' + dateInterval.invalidReason);
  }
  return dateInterval;
};

const useDateRange = (defaultValue = Period.SEVEN_DAYS_AGO) => {
  const [periodQueryParam, setPeriodQueryParam] = useQueryParam(
    'period',
    withDefault(StringParam, defaultValue, true),
    { removeDefaultsFromUrl: true, updateType: 'replaceIn' },
  );

  // on initial render of an Insights page read date range value from URL
  const [dateRange, setDateRange] = useState<DateRange>(parsePeriod(periodQueryParam));

  useEffect(
    // reflect date range value in URL, so that it could be shared
    () => setPeriodQueryParam(serializePeriod(dateRange)),
    [
      dateRange, // update it when user changes date range
      periodQueryParam, // update it on navigation when URL changes and the param is removed
      setPeriodQueryParam,
    ],
  );

  return {
    setDateRange,
    dateInterval: rangeToInterval(dateRange),
    dateRange,
  };
};

export default useDateRange;
